// src/utils/followUpNotify.js
// Option A delivery: when the app loads, fire one push per newly-due touch
// for the signed-in user, then stamp notifiedAt so each touch notifies once.
// No server cron; if nobody opens the app, touches wait as overdue rows.
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { newmessagesuccess } from "./helper";
import {
  selectNotifiable, touchLabel, missedClosings, retractablePlans, planAgentBackfills,
} from "./followUpPlan";
import {
  updateTouch, rollAnniversaryForward, ensureFollowUpPlan, retractFollowUpPlan,
  reassignFollowUpPlan,
} from "./followUpPlansStore";

export async function notifyDueFollowUps(user, deals) {
  if (!user?.id) return;
  try {
    const snap = await getDocs(
      query(
        collection(firestored, "followUpPlans"),
        where("agentId", "==", user.id),
      ),
    );
    const plans = snap.docs.map((d) => ({ ...d.data(), dealId: d.id }));

    // A plan doc FREEZES an agentId the day it is written; who a deal credits
    // is recomputed from the deal every single time it is asked. So a plan
    // enrolled before the credit rule was settled can sit in the wrong
    // person's queue forever, notifying him about a past client every report
    // says belongs to someone else. planAgentBackfills lists ONLY the plans
    // that disagree with their own deal, so once this has run, a second run
    // finds nothing and writes nothing. No plan doc is created or destroyed
    // here: it is one field, one write, on plans that were already wrong.
    //
    // Scope: the query above returns only plans currently stamped to the
    // signed-in user, so it is each person's own login that clears a plan out
    // of their queue. That is the direction that matters, because whoever is
    // wrongly holding a plan is exactly the person being wrongly notified
    // about it, and it needs no wider read than the sweep already does.
    //
    // Its own try/catch: a failed write must never cost this user the
    // due-touch pushes below.
    try {
      for (const fix of planAgentBackfills(plans, deals)) {
        await reassignFollowUpPlan(fix.planId, fix.agentId);
        // Correct the in-memory copy as each write lands, not in one batch
        // afterwards. The rest of this pass then treats a plan that has just
        // moved to somebody else as no longer this user's and stops short of
        // pushing him one last notification about it. It also means a write
        // that fails half way through a list still leaves every already-moved
        // plan correctly marked, instead of pushing the old owner about a plan
        // that is now the new owner's too.
        const local = plans.find((p) => p.dealId === fix.planId);
        if (local) local.agentId = fix.agentId;
      }
    } catch (e) {
      console.error("Follow-up plan owner backfill failed:", e);
    }

    const activePlans = plans.filter(
      (p) => p.status === "active" && p.agentId === user.id,
    );

    // Backstop: closings that bypassed the board/stage-pill hooks (edit-form
    // saves, bulk upload, integrations) self-heal here on the owner's next
    // app load. PROGRAM_START keeps old closings out of the program (see
    // missedClosings for the full filter).
    const missed = missedClosings(deals, plans, user.id);
    for (const deal of missed) {
      await ensureFollowUpPlan(deal);
    }

    // Self-heal the other direction too: only the stage-pill hook and the
    // board's handleDragEnd retract a plan directly when a deal moves off
    // Closed. Anything else that changes stage — an edit-form save, a bulk
    // upload, an integration — heals here on the owner's next login.
    const toRetract = retractablePlans(plans, deals);
    const retractedIds = new Set(toRetract.map((p) => p.dealId));
    for (const plan of toRetract) {
      await retractFollowUpPlan(plan.dealId);
    }
    const livePlans = activePlans.filter((p) => !retractedIds.has(p.dealId));

    // (Plans created just now notify on the NEXT load; keeping this pass
    // simple beats re-querying.)
    // Keep anniversaries fresh while we're here (stale ones roll forward).
    // rollAnniversaryForward returns the entry it just wrote so the in-memory
    // plan can be corrected before selectNotifiable runs below — otherwise a
    // touch just marked "skipped" in Firestore would still read "pending"
    // here and fire a ghost push for something already resolved.
    for (const plan of livePlans) {
      const entry = await rollAnniversaryForward(plan);
      if (entry) {
        if (entry.staleKey && plan.touches[entry.staleKey]) {
          plan.touches[entry.staleKey] = { ...plan.touches[entry.staleKey], state: "skipped" };
        }
        plan.touches[entry.key] = entry.touch;
      }
    }
    const due = selectNotifiable(livePlans, user.id, deals);
    for (const item of due) {
      await updateTouch(item.planId, item.touchKey, { notifiedAt: Date.now() });
      await newmessagesuccess(
        "Client check-in due",
        user.id,
        `${touchLabel(item.touchKey)}: ${item.clientNames}`,
        `${item.planId}:${item.touchKey}`,
        "followup",
        item.planId,
      );
    }
  } catch (e) {
    // Never let follow-up notification failures affect app load.
    console.error("Follow-up notify failed:", e);
  }
}
